import { useEffect, useRef, useState } from 'react'
import { Input } from '@highlight/ui'
import { Search } from 'lucide-react'

const DEBOUNCE_MS = 200

interface ViewSearchInputProps {
  value: string
  onChange: (value: string) => void
  placeholder: string
  /** Accessible name — views share the pill but search different things. */
  label: string
}

/**
 * The search pill used across full-list Views. Debounced so typing doesn't
 * re-filter (or re-query) on every keystroke, while the field itself stays
 * uncontrolled-feeling.
 */
export function ViewSearchInput({ value, onChange, placeholder, label }: ViewSearchInputProps) {
  const ref = useRef<HTMLInputElement>(null)
  const [internalValue, setInternalValue] = useState(value)

  useEffect(() => {
    setInternalValue(value)
  }, [value])

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (internalValue !== value) {
        onChange(internalValue)
      }
    }, DEBOUNCE_MS)
    return () => clearTimeout(timeout)
  }, [internalValue, onChange, value])

  return (
    <div className="relative w-full">
      <Input
        ref={ref}
        size="xl"
        type="text"
        value={internalValue}
        onChange={(e) => setInternalValue(e.target.value)}
        placeholder={placeholder}
        aria-label={label}
        spellCheck={false}
        className="pl-9"
      />
      {/* After the Input: the field is positioned with a solid ground, so an earlier sibling sits under it. */}
      <Search
        className="text-foreground-secondary pointer-events-none absolute left-3 top-1/2 size-3.5 -translate-y-1/2"
        aria-hidden="true"
      />
    </div>
  )
}
